import { useRef, useState } from 'react';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { Box, GlobalStyles, Grid, IconButton, InputAdornment, Popper, TextField } from '@mui/material';
import { setHours, setMinutes, setSeconds } from 'date-fns';
import { enGB } from 'date-fns/locale/en-GB';
import { nl } from 'date-fns/locale/nl';
import { SHORT_DATE_TIME_FORMAT } from '../../../utils/constants';
import { DatePickerInputProps } from './DatePickerInput.types';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Icons } from 'greener-ui-library';

registerLocale('nl', nl);
registerLocale('en', enGB);
setDefaultLocale('en');

export function DatePickerInput<C extends FieldValues>({
  maxDate,
  minDate,
  control,
  name,
  label,
  showTimeInput = true,
  gridSize = 6,
  fullWidth = true,
  variant = 'outlined',
  readOnly = false,
  shrink = true,
  defaultValue = '',
  size = 'small',
  dateFormat = SHORT_DATE_TIME_FORMAT,
  initialHours = null,
  initialMinutes = null,
  initialSeconds = null,
  placeholder,
  isClearable = false,
  onChange: customOnChange,
  ...props
}: DatePickerInputProps<C>) {
  const {
    field: { ref, onBlur, value, onChange, ...inputProps },
    fieldState: { error, isDirty },
  } = useController({
    name: name as Path<C>,
    control,
    defaultValue: defaultValue as PathValue<C, Path<C>>,
  });
  const { t, i18n } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  function onChangeDate(date: Date) {
    // Prevent trying to format date with a null value
    if (!date) {
      onChange(undefined);
      customOnChange?.({ date, event: undefined });
      return;
    }

    // For some reason, we have to check for 'dirty' here, instead of 'touched'.
    if (initialHours && !isDirty) {
      date = setHours(date, initialHours);
    }
    if (initialMinutes && !isDirty) {
      date = setMinutes(date, initialMinutes);
    }
    if (initialSeconds && !isDirty) {
      date = setSeconds(date, initialSeconds);
    }
    onChange(date);
    customOnChange?.({ date, event: undefined });
  }

  function handleClear() {
    onChange(undefined);
    customOnChange?.({ date: null, event: undefined });
  }

  return (
    <Grid ref={anchorRef} item xs={gridSize}>
      <GlobalStyles
        styles={{
          '.react-datepicker-wrapper': { width: '100%', display: 'block' },
          '.react-datepicker__input-container': { width: '100%', display: 'block' },
        }}
      />
      {/* Make sure the datepicker takes full width */}
      <DatePicker
        selected={value}
        maxDate={maxDate}
        minDate={minDate}
        autoComplete="off"
        locale={i18n.language}
        timeFormat="HH:mm"
        dateFormat={dateFormat}
        showTimeSelect={showTimeInput}
        timeIntervals={60}
        onBlur={onBlur}
        readOnly={readOnly}
        calendarStartDay={1} // Have weeks start on a monday (1) instead of a sunday (0).
        onCalendarOpen={() => setIsOpen(true)}
        onCalendarClose={() => setIsOpen(false)}
        placeholderText={placeholder}
        isClearable={false}
        popperContainer={({ children }) => (
          <Popper
            open={!!anchorRef.current}
            anchorEl={anchorRef.current}
            sx={{
              minWidth: '350px',
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            {children}
          </Popper>
        )}
        customInput={
          <TextField
            ref={ref}
            error={!!error}
            helperText={error && t(error.message as string)}
            variant={variant}
            fullWidth={fullWidth}
            label={label}
            autoComplete="new-password" // Prevents automatic form filling by browser
            InputLabelProps={{ shrink: shrink }}
            inputProps={{ readOnly }}
            size={size}
            disabled={readOnly}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isClearable && value && !readOnly && (
                      <IconButton size="small" onClick={handleClear}>
                        <Icons.Close fontSize="small" />
                      </IconButton>
                    )}
                    {isOpen ? <Icons.ArrowDropUp /> : <Icons.ArrowDropDown />}
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        }
        {...inputProps}
        {...props}
        onChange={onChangeDate}
      />
    </Grid>
  );
}
